import { Hammer, ArrowRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { LuxuryTable, SectionTitle } from '../Luxury';
import { Check } from 'lucide-react';

export interface CapExSystem {
  name: string;
  description: string;
  cost: number;
  perUnit?: boolean;
}

interface CapExSizingLogic {
  rentUpliftPerUnit: number;
  totalUnits: number;
  annualNoiUplift: number;
  roiTarget: number; // e.g. 0.20 for 20%
}

interface CapExBreakdownProps {
  systems: CapExSystem[];
  totalUnits: number;
  totalCapEx: number;
  sizingLogic?: CapExSizingLogic;
  roiPercent?: number;      // Annual NOI uplift / total CapEx (actual result, e.g., 0.08 for 8%)
  roiTargetPct?: number;    // Target ROI used for sizing (e.g., 0.20 for 20%) — default 0.20
  paybackYears?: number;    // Inverse: total CapEx / annual NOI uplift
  roiDerivedBudget?: number; // Budget at target ROI (annualNoiUplift / roiTarget)
  ssotPerUnit?: number;          // SSOT per-unit default for winner display
  capexFormulaWinner?: string;   // 'ssot_default' | 'roi_target' | 'user_override'
  annualNoiUplift?: number;      // Annual NOI uplift for ROI-target formula display
  /** CRH-01: Callback called when user clicks a preset button. Parent triggers what-if recalculation. */
  onCapexPresetSelect?: (capexValue: number) => void;
  /** CRH-01: Which preset is currently active (for visual highlight). */
  activeCapexPreset?: 'light' | 'full' | null;
  className?: string;
}

/**
 * CapExBreakdown component displays a system-by-system breakdown of capital expenditures.
 *
 * Features:
 * - System-by-system table (flooring, kitchen, bathroom, HVAC, electrical, plumbing, exterior, contingency)
 * - Per-unit calculations shown (e.g., "$8,500 x 3 units = $25,500")
 * - Contingency row highlighted in amber (10%)
 * - Total row with bold styling and gold accent
 * - Uses LuxuryTable for dark theme styling
 * - JetBrains Mono for all dollar amounts
 */
// Better descriptions for system names from config
const SYSTEM_DESCRIPTIONS: Record<string, string> = {
  kitchen: 'Cabinets, countertops, appliances, fixtures',
  bathroom: 'Vanity, tile, fixtures, tub/shower',
  flooring: 'LVP/hardwood throughout living areas',
  hvac: 'Heating/cooling system upgrade or replacement',
  electrical: 'Panel upgrade, wiring, outlets, lighting',
  plumbing: 'Supply lines, drain replacement, water heater',
  exterior: 'Roof, siding, windows, curb appeal',
  contingency: '10% reserve for unforeseen costs',
};

function getSystemDescription(name: string): string {
  const key = name.toLowerCase().replace(/[^a-z]/g, '');
  for (const [pattern, desc] of Object.entries(SYSTEM_DESCRIPTIONS)) {
    if (key.includes(pattern)) return desc;
  }
  return `${name} renovation`;
}

export function CapExBreakdown({
  systems,
  totalUnits,
  totalCapEx,
  sizingLogic,
  roiPercent,
  roiTargetPct = 0.20,
  paybackYears,
  roiDerivedBudget,
  ssotPerUnit,
  capexFormulaWinner,
  annualNoiUplift: noiUpliftProp,
  onCapexPresetSelect,
  activeCapexPreset,
  className = '',
}: CapExBreakdownProps) {
  // Calculate totals
  const systemsSubtotal = systems.reduce((sum, sys) => {
    const lineTotal = sys.perUnit ? sys.cost * totalUnits : sys.cost;
    return sum + lineTotal;
  }, 0);

  // Check if contingency is included in systems
  const hasContingency = systems.some(
    (sys) => sys.name.toLowerCase().includes('contingency')
  );

  // If no contingency in systems and totalCapEx > systemsSubtotal, calculate it
  const contingencyAmount = hasContingency
    ? 0
    : totalCapEx - systemsSubtotal;

  const showContingencyRow = !hasContingency && contingencyAmount > 0;

  return (
    <div className={`bg-obsidian rounded-xl p-6 ${className}`}>
      <SectionTitle icon={<Hammer className="w-5 h-5" />}>
        Capital Expenditure Breakdown
      </SectionTitle>

      {/* Phase 212: Budget Sizing Formula - winner-aware, replaces misleading "sized from ROI target" */}
      {sizingLogic && sizingLogic.rentUpliftPerUnit > 0 && (
        <div className="mb-6 mt-4">
          <h4 className="text-sm uppercase tracking-[0.12em] text-stone-400 font-semibold mb-3">
            Budget Sizing Formula
          </h4>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Box 1: Rent Uplift Input */}
            <div className="flex-1 min-w-[140px] p-3 bg-gold/5 border border-gold/30 rounded-lg text-center">
              <div className="text-sm uppercase tracking-wider text-stone-400 mb-1">Input</div>
              <div className="text-lg font-mono tabular-nums font-bold text-gold">
                +{formatCurrency(sizingLogic.rentUpliftPerUnit)}/unit
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
            {/* Box 2: Annual NOI Uplift */}
            <div className="flex-1 min-w-[140px] p-3 bg-emerald/5 border border-emerald/30 rounded-lg text-center">
              <div className="text-sm uppercase tracking-wider text-stone-400 mb-1">
                Annual NOI Uplift ({sizingLogic.totalUnits} units)
              </div>
              <div className="text-lg font-mono tabular-nums font-bold text-emerald">
                +{formatCurrency(sizingLogic.annualNoiUplift)}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
            {/* Box 3: Winner-aware budget display */}
            {capexFormulaWinner === 'ssot_default' && ssotPerUnit != null ? (
              <div className="flex-1 min-w-[160px] p-3 bg-gold/5 border border-gold/30 rounded-lg text-center">
                <div className="text-xs uppercase tracking-wider text-gold/70 mb-1 font-semibold">
                  Budget &mdash; SSOT Default
                </div>
                <div className="text-base font-mono tabular-nums font-bold text-gold">
                  {formatCurrency(ssotPerUnit)}/unit &times; {sizingLogic.totalUnits}
                </div>
                <div className="text-lg font-mono font-semibold text-ivory mt-1">
                  = {formatCurrency(totalCapEx)}
                </div>
                {(noiUpliftProp ?? sizingLogic.annualNoiUplift) > 0 && (
                  <div className="text-xs text-stone-400 mt-1.5 leading-relaxed">
                    ROI-target: {formatCurrency((noiUpliftProp ?? sizingLogic.annualNoiUplift) / 0.20)} &mdash; SSOT default used (ensures min renovation quality)
                  </div>
                )}
              </div>
            ) : capexFormulaWinner === 'roi_target' ? (
              <div className="flex-1 min-w-[160px] p-3 bg-emerald/5 border border-emerald/20 rounded-lg text-center">
                <div className="text-xs uppercase tracking-wider text-emerald/70 mb-1 font-semibold">
                  Budget &mdash; ROI Target
                </div>
                <div className="text-lg font-mono tabular-nums font-bold text-emerald">
                  {formatCurrency(totalCapEx)}
                </div>
                <div className="text-xs text-stone-400 mt-1.5">
                  {formatCurrency(sizingLogic.annualNoiUplift)}/yr / {Math.round(sizingLogic.roiTarget * 100)}% ROI target
                </div>
                {ssotPerUnit != null && (
                  <div className="text-xs text-stone-400 mt-1">
                    SSOT default: {formatCurrency(ssotPerUnit)}/unit &mdash; ROI formula used (right-sized to uplift)
                  </div>
                )}
              </div>
            ) : capexFormulaWinner === 'user_override' ? (
              <div className="flex-1 min-w-[160px] p-3 bg-ivory/5 border border-ivory/30 rounded-lg text-center">
                <div className="text-xs uppercase tracking-wider text-stone-400 mb-1 font-semibold">
                  Budget &mdash; User Specified
                </div>
                <div className="text-lg font-mono tabular-nums font-bold text-ivory">
                  {formatCurrency(totalCapEx)}
                </div>
                <div className="text-xs text-stone-400 mt-1.5">
                  {formatCurrency(totalCapEx / Math.max(sizingLogic.totalUnits, 1))}/unit (manual override)
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-[140px] p-3 bg-ivory/5 border border-ivory/30 rounded-lg text-center">
                <div className="text-sm uppercase tracking-wider text-stone-400 mb-1">
                  Actual Budget
                </div>
                <div className="text-lg font-mono tabular-nums font-bold text-ivory">
                  {formatCurrency(totalCapEx)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <LuxuryTable>
        <thead>
          <tr>
            <th>System</th>
            <th>Description</th>
            <th className="text-right">Per Unit</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {systems.map((system, idx) => {
            const isContingency = system.name.toLowerCase().includes('contingency');
            const lineTotal = system.perUnit
              ? system.cost * totalUnits
              : system.cost;

            return (
              <tr
                key={idx}
                className={isContingency ? 'bg-amber-900/20' : ''}
              >
                <td className={isContingency ? 'text-amber-400 font-medium' : ''}>
                  {system.name}
                </td>
                <td className="text-stone-400 text-[12px]">
                  {getSystemDescription(system.name)}
                </td>
                <td className="num">
                  <span className="text-stone-400">
                    {formatCurrency(lineTotal / Math.max(totalUnits, 1))}
                  </span>
                </td>
                <td className={`num ${isContingency ? 'text-amber-400' : ''}`}>
                  {formatCurrency(lineTotal)}
                </td>
              </tr>
            );
          })}

          {/* Contingency row if not included in systems */}
          {showContingencyRow && (
            <tr className="bg-amber-900/20">
              <td className="text-amber-400 font-medium">Contingency</td>
              <td className="text-stone-400 text-[12px]">
                10% reserve for unforeseen costs
              </td>
              <td className="num">
                <span className="text-stone-400">--</span>
              </td>
              <td className="num text-amber-400">
                {formatCurrency(contingencyAmount)}
              </td>
            </tr>
          )}

          {/* Total row */}
          <tr className="total-row">
            <td colSpan={3} className="font-bold">
              Total CapEx Budget
            </td>
            <td className="num">
              {formatCurrency(totalCapEx)}
            </td>
          </tr>
        </tbody>
      </LuxuryTable>

      {/* CRH-01: CapEx Range — Interactive preset buttons (Light Refresh / Full Renovation) */}
      {roiDerivedBudget != null && roiDerivedBudget > 0 && roiDerivedBudget < totalCapEx && (
        <div className="mt-4 p-3 bg-charcoal/40 border border-slate-dark rounded-lg">
          <div className="text-xs uppercase tracking-wider text-stone-400 mb-2">CapEx Range</div>
          <div className="grid grid-cols-2 gap-4">
            {/* Light Refresh preset */}
            {(() => {
              const isActive = activeCapexPreset === 'light';
              const isInteractive = !!onCapexPresetSelect;
              return (
                <div
                  className={`p-3 rounded-lg border transition-all duration-200 ${
                    isInteractive
                      ? isActive
                        ? 'border-gold bg-gold/10 ring-1 ring-gold/60'
                        : 'border-slate-dark bg-charcoal/40 hover:border-stone-500 cursor-pointer'
                      : 'border-slate-dark bg-charcoal/40'
                  }`}
                  onClick={isInteractive ? () => onCapexPresetSelect(roiDerivedBudget) : undefined}
                  role={isInteractive ? 'button' : undefined}
                  tabIndex={isInteractive ? 0 : undefined}
                  onKeyDown={isInteractive ? (e) => e.key === 'Enter' && onCapexPresetSelect(roiDerivedBudget) : undefined}
                  aria-pressed={isInteractive ? isActive : undefined}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-stone-400">Light Refresh</div>
                    {isActive && <Check className="w-3.5 h-3.5 text-gold" />}
                  </div>
                  <div className="text-lg font-mono tabular-nums font-bold text-emerald">
                    {formatCurrency(roiDerivedBudget)}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {formatCurrency(roiDerivedBudget / Math.max(totalUnits, 1))}/unit &bull; 20% ROI &bull; Kitchen/bath cosmetics
                  </div>
                </div>
              );
            })()}

            {/* Full Renovation preset */}
            {(() => {
              const isActive = activeCapexPreset === 'full';
              const isInteractive = !!onCapexPresetSelect;
              return (
                <div
                  className={`p-3 rounded-lg border transition-all duration-200 ${
                    isInteractive
                      ? isActive
                        ? 'border-gold bg-gold/10 ring-1 ring-gold/60'
                        : 'border-slate-dark bg-charcoal/40 hover:border-stone-500 cursor-pointer'
                      : 'border-slate-dark bg-charcoal/40'
                  }`}
                  onClick={isInteractive ? () => onCapexPresetSelect(totalCapEx) : undefined}
                  role={isInteractive ? 'button' : undefined}
                  tabIndex={isInteractive ? 0 : undefined}
                  onKeyDown={isInteractive ? (e) => e.key === 'Enter' && onCapexPresetSelect(totalCapEx) : undefined}
                  aria-pressed={isInteractive ? isActive : undefined}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-stone-400">Full Renovation</div>
                    {isActive && <Check className="w-3.5 h-3.5 text-gold" />}
                  </div>
                  <div className="text-lg font-mono tabular-nums font-bold text-gold">
                    {formatCurrency(totalCapEx)}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {formatCurrency(totalCapEx / Math.max(totalUnits, 1))}/unit &bull; {roiPercent != null ? `${(roiPercent * 100).toFixed(0)}%` : '--'} ROI &bull; Gut reno (SSOT default)
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="text-xs text-stone-500 mt-2">
            {onCapexPresetSelect
              ? 'Select a CapEx level to recalculate the entire deal. You can fine-tune in the Assumptions drawer.'
              : 'Walk the property to determine where on this range to budget. Use the Assumptions button above to adjust CapEx and see returns recalculate.'
            }
          </div>
        </div>
      )}

      {/* Per-unit summary with inline ROI stat */}
      <div className="mt-3 flex items-center gap-4 flex-wrap text-sm text-ivory/70">
        <span>
          Per Unit:{' '}
          <span className="font-mono tabular-nums font-medium text-ivory">
            {formatCurrency(totalCapEx / totalUnits)}
          </span>
        </span>
        {roiPercent != null && (
          <>
            <span className="text-stone-400">|</span>
            <span>
              Actual ROI:{' '}
              <span className={`font-mono tabular-nums font-semibold ${roiPercent >= roiTargetPct ? 'text-gold' : roiPercent >= roiTargetPct * 0.75 ? 'text-amber-400' : 'text-crimson'}`}>
                {(roiPercent * 100).toFixed(1)}%
              </span>
            </span>
            <span className="text-stone-400">|</span>
            <span>
              Target:{' '}
              <span className="font-mono tabular-nums font-medium text-stone-400">
                {(roiTargetPct * 100).toFixed(0)}%
              </span>
            </span>
            {paybackYears != null && (
              <>
                <span className="text-stone-400">|</span>
                <span>
                  Payback:{' '}
                  <span className="font-mono tabular-nums font-medium text-ivory">
                    {paybackYears.toFixed(1)} yr
                  </span>
                </span>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
